"use client";

import { Vehicle, VehicleFilter } from "@alba-cars/common-modules";
import React, { useCallback, useEffect, useState } from "react";

import FilterDropdownFullScreen from "@/app/buy-used-cars-uae/_components/FullScreenDialog";
import PriceFilter from "@/app/buy-used-cars-uae/_components/PriceFilter";
import { BodyTypeOption, MakeOption, ModelOption } from "@/app/buy-used-cars-uae/_components/types";
import { YearFilter } from "@/app/buy-used-cars-uae/_components/YearFilter";
import { useVehicleFiltersData } from "@/app/buy-used-cars-uae/_hooks/useVehicleFilterData";
import CustomButton from "@/components/ui/buttons/Button";
import FilterDropdown from "@/components/ui/FilterDropdown";
import Modal from "@/components/ui/Modal";
import SectionTitle from "@/components/ui/SectionTitle";
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface CompareFilterProps {
  handleFilterChange: (newFilterValues: Partial<VehicleFilter>) => Promise<Vehicle[] | undefined>;
  handleResetFilter: () => void;
  resultsCount?: number;
  isLoading?: boolean;
  open?: boolean;
  setOpen?: (isOpen: boolean) => void;
}

const FilterContent: React.FC<{
  formState: VehicleFilter;
  updateFormState: (field: string, value: any) => void;
  localMakes: MakeOption[] | null;
  setLocalMakes: React.Dispatch<React.SetStateAction<MakeOption[] | null>>;
  localModels: ModelOption[] | null;
  setLocalModels: React.Dispatch<React.SetStateAction<ModelOption[] | null>>;
  localBodyTypes: BodyTypeOption[] | null;
  setLocalBodyTypes: React.Dispatch<React.SetStateAction<BodyTypeOption[] | null>>;
  handleApplyFilters: () => Promise<void>;
  handleReset: () => void;
  hasAppliedFilters: boolean;
  isFiltering: boolean;
  isLoading: boolean;
  noResults: boolean;
  resultsCount?: number;
  setSelectedMakes?: (makes: string[] | undefined) => void;
  makesOptions: MakeOption[] | undefined;
  modelsOptions: ModelOption[] | undefined;
  bodyTypeOptions: BodyTypeOption[] | undefined;
  isMakesOptionsLoading: boolean;
  isModelsOptionsLoading: boolean;
  isBodyTypeOptionsLoading: boolean;
  isMobile: boolean;
  onClose?: () => void;
}> = ({
  formState,
  updateFormState,
  localMakes,
  setLocalMakes,
  localModels,
  setLocalModels,
  localBodyTypes,
  setLocalBodyTypes,
  handleApplyFilters,
  handleReset,
  hasAppliedFilters,
  isFiltering,
  isLoading,
  noResults,
  resultsCount,
  setSelectedMakes,
  makesOptions,
  modelsOptions,
  bodyTypeOptions,
  isMakesOptionsLoading,
  isModelsOptionsLoading,
  isBodyTypeOptionsLoading,
  isMobile,
  onClose,
}) => {
  const hasSelectedFilters =
    // Check for makes, models, body types
    (localMakes?.length ?? 0) > 0 ||
    (localModels?.length ?? 0) > 0 ||
    (localBodyTypes?.length ?? 0) > 0 ||
    // Check price range
    formState?.price?.min !== undefined ||
    formState?.price?.max !== undefined ||
    // Check year range
    formState?.year?.min !== undefined ||
    formState?.year?.max !== undefined;

  return (
    <div className={`flex flex-col ${isMobile ? "h-full" : "h-[900px]"}`}>
      {/* Scrollable content area */}
      <div className={`no-scrollbar flex-1 overflow-y-auto ${isMobile ? "pb-32" : "pb-28"}`}>
        {/* Show "No results" message when appropriate */}
        {hasAppliedFilters && !isLoading && (resultsCount === 0 || noResults) && (
          <div className="mb-6 rounded-xl bg-gray-50 p-4 text-center">
            <p className="font-medium text-gray-800">No results available</p>
            <p className="mt-2 text-sm text-gray-600">Please try different filter criteria</p>
            <CustomButton
              className="mt-4 w-full rounded-lg bg-gray-200 py-2 font-medium text-gray-800 transition-colors hover:bg-gray-300"
              onClick={handleReset}
              buttonText="Reset Filters"
            />
          </div>
        )}

        {/* Description */}
        <div className="inter mb-4 hidden rounded-xl text-sm font-semibold text-primary-foreground lg:flex lg:text-xl lg:text-gray-800">
          Select and filter the cars you want to compare. Adjust features, specifications, and preferences to find the
          perfect match
        </div>

        {/* Price Filter */}
        <div className="m-1 mb-3">
          <PriceFilter
            onFromPriceChange={(fromPrice) => {
              const fromPriceValue = (fromPrice as unknown as { value: number; label: string })?.value;
              updateFormState("price", {
                ...(formState?.price ?? {}),
                min: fromPriceValue,
              });
            }}
            onFromPriceClear={() => {
              updateFormState("price", {
                ...(formState?.price ?? {}),
                min: undefined,
              });
            }}
            onToPriceChange={(toPrice) => {
              const toPriceValue = (toPrice as unknown as { value: number; label: string })?.value;
              updateFormState("price", {
                ...(formState?.price ?? {}),
                max: toPriceValue,
              });
            }}
            onToPriceClear={() => {
              updateFormState("price", {
                ...(formState?.price ?? {}),
                max: undefined,
              });
            }}
            onReset={() => {
              updateFormState("price", undefined);
            }}
            fromValue={
              formState?.price?.min ? { value: formState.price.min, label: formState.price.min.toString() } : null
            }
            toValue={
              formState?.price?.max ? { value: formState.price.max, label: formState.price.max.toString() } : null
            }
          />
        </div>

        {/* Year Filter */}
        <div className="m-1 mb-3">
          <YearFilter
            onFromYearChange={(fromYear) => {
              const fromYearValue = (fromYear as unknown as { value: number; label: string })?.value;
              updateFormState("year", {
                ...(formState?.year ?? {}),
                min: Math.round(fromYearValue),
              });
            }}
            onFromYearClear={() => {
              updateFormState("year", {
                ...(formState?.year ?? {}),
                min: undefined,
              });
            }}
            onToYearChange={(toYear) => {
              const toYearValue = (toYear as unknown as { value: number; label: string })?.value;
              updateFormState("year", {
                ...(formState?.year ?? {}),
                max: Math.round(toYearValue),
              });
            }}
            onToYearClear={() => {
              updateFormState("year", {
                ...(formState?.year ?? {}),
                max: undefined,
              });
            }}
            onReset={() => updateFormState("year", undefined)}
            fromValue={
              formState?.year?.min ? { value: formState.year.min, label: formState.year.min.toString() } : null
            }
            toValue={formState?.year?.max ? { value: formState.year.max, label: formState.year.max.toString() } : null}
          />
        </div>

        {/* Make Filter */}
        <div className="mx-1 mb-3">
          <p className="mb-2 font-medium text-gray-800">Make</p>
          {isMobile ? (
            <FilterDropdownFullScreen
              isSearchable={false}
              id="make-filter"
              options={makesOptions ?? []}
              isLoading={isMakesOptionsLoading}
              helperText="Make"
              onSelect={(options) => {
                const selectedOptions: MakeOption[] = Array.isArray(options) ? options : [options];
                setSelectedMakes?.(selectedOptions.map((option: MakeOption) => option.value));
                setLocalMakes(selectedOptions);
              }}
              onClear={() => {
                setLocalMakes(null);
                setLocalModels(null);
              }}
              value={localMakes as any}
              isMulti
            />
          ) : (
            <FilterDropdown
              id="make-filter"
              options={makesOptions ?? []}
              isLoading={isMakesOptionsLoading}
              helperText="Make"
              onSelect={(options) => {
                const selectedOptions: MakeOption[] = Array.isArray(options) ? options : [options];
                setSelectedMakes?.(selectedOptions.map((option: MakeOption) => option.value));
                setLocalMakes(selectedOptions);
              }}
              onClear={() => {
                setLocalMakes(null);
                setLocalModels(null);
              }}
              value={localMakes as any}
              isMulti
            />
          )}
        </div>

        {/* Model Filter - depends on make selection */}
        <div className="mx-1 mb-3">
          {isMobile ? (
            localMakes?.length && (
              <FilterDropdownFullScreen
                id="model-filter"
                options={modelsOptions ?? []}
                isSearchable={false}
                isLoading={isModelsOptionsLoading}
                helperText="Model"
                withLabel={true}
                onSelect={(options) => {
                  const selectedOptions: ModelOption[] = Array.isArray(options) ? options : [options];
                  setLocalModels(selectedOptions);
                }}
                onClear={() => setLocalModels(null)}
                value={localModels as any}
                isMulti
                customNoOptionsMessage={localMakes?.length ? "No models available" : "Please select a make first"}
                customOption={(option: unknown) => (
                  <div className="inter flex items-center gap-2">
                    <span className="text-base font-medium">{(option as ModelOption).make.name}</span>
                    <span className="text-base font-medium">-</span>
                    <span className="text-base font-medium">{(option as ModelOption).label}</span>
                  </div>
                )}
              />
            )
          ) : (
            <FilterDropdown
              id="model-filter"
              options={modelsOptions ?? []}
              isLoading={isModelsOptionsLoading}
              helperText="Model"
              withLabel={true}
              onSelect={(options) => {
                const selectedOptions: ModelOption[] = Array.isArray(options) ? options : [options];
                setLocalModels(selectedOptions);
              }}
              onClear={() => setLocalModels(null)}
              value={localModels as any}
              isMulti
              customNoOptionsMessage={() => (localMakes?.length ? "No models available" : "Please select a make first")}
            />
          )}
        </div>

        {/* Body Type Filter */}
        <div className="mx-1 mb-3">
          {isMobile ? (
            <FilterDropdownFullScreen
              id="bodyType-filter"
              options={bodyTypeOptions ?? []}
              isSearchable={false}
              isLoading={isBodyTypeOptionsLoading}
              helperText="Body Type"
              withLabel={true}
              onSelect={(options) => {
                const selectedOptions: BodyTypeOption[] = Array.isArray(options) ? options : [options];
                setLocalBodyTypes(selectedOptions);
              }}
              onClear={() => setLocalBodyTypes(null)}
              value={localBodyTypes as any}
              isMulti
            />
          ) : (
            <FilterDropdown
              id="bodyType-filter"
              options={bodyTypeOptions ?? []}
              isLoading={isBodyTypeOptionsLoading}
              helperText="Body Type"
              withLabel={true}
              onSelect={(options) => {
                const selectedOptions: BodyTypeOption[] = Array.isArray(options) ? options : [options];
                setLocalBodyTypes(selectedOptions);
              }}
              onClear={() => setLocalBodyTypes(null)}
              value={localBodyTypes as any}
              isMulti
            />
          )}
        </div>
      </div>

      {/* Submit and Reset buttons - sticky at bottom */}
      <div
        className={
          isMobile
            ? "fixed bottom-0 left-0 right-0 z-20 border-t border-gray-100 bg-white px-4 pb-8 pt-4 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.05)]"
            : "absolute bottom-0 left-0 right-0 rounded-b-3xl bg-white p-6 pt-4 shadow-[0_-2px_3px_-1px_rgba(0,0,0,0.05)]"
        }
      >
        <div className={isMobile ? "flex gap-3" : ""}>
          {hasAppliedFilters && isMobile && (
            <CustomButton
              className="flex-1 rounded-xl border border-gray-200 py-3 font-medium text-primary-foreground transition-colors hover:bg-gray-50"
              onClick={handleReset}
              buttonText="Reset"
            />
          )}
          <CustomButton
            className={`${isMobile ? "flex-1" : "w-full"} rounded-xl bg-primary py-3 font-medium text-white transition-colors duration-300 hover:bg-primary-dark disabled:cursor-not-allowed disabled:bg-secondary disabled:text-gray-300 ${isFiltering || isLoading ? "opacity-80" : ""}`}
            onClick={async () => {
              await handleApplyFilters();
              if (onClose) onClose();
            }}
            buttonText={isFiltering || isLoading ? "Loading..." : "Show Results"}
            disabled={isFiltering || isLoading || !hasSelectedFilters}
          />
        </div>

        {hasAppliedFilters && !isMobile && (
          <CustomButton
            className="mt-3 w-full rounded-lg py-3 font-medium text-primary-foreground transition-colors hover:bg-gray-50"
            onClick={handleReset}
            buttonText="Reset"
          />
        )}
      </div>
    </div>
  );
};

const CompareCarFilters: React.FC<CompareFilterProps> = ({
  handleFilterChange,
  handleResetFilter,
  resultsCount,
  isLoading = false,
  open: externalOpen,
  setOpen: setExternalOpen,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);

  // Use either the external open state (controlled by parent) or internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = setExternalOpen || setInternalOpen;
  const [isFiltering, setIsFiltering] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const {
    makesOptions,
    modelsOptions,
    bodyTypeOptions,
    isMakesOptionsLoading,
    isModelsOptionsLoading,
    isBodyTypeOptionsLoading,
    setMakes: setSelectedMakes,
  } = useVehicleFiltersData();

  // Local state for form values (before submit)
  const [formState, setFormState] = useState<VehicleFilter>({});

  // Local state for dropdown selections
  const [localMakes, setLocalMakes] = useState<MakeOption[] | null>(null);
  const [localModels, setLocalModels] = useState<ModelOption[] | null>(null);
  const [localBodyTypes, setLocalBodyTypes] = useState<BodyTypeOption[] | null>(null);

  // Handle form reset
  const handleReset = useCallback((): void => {
    // Reset local form state
    setFormState(new VehicleFilter());
    setLocalMakes(null);
    setLocalModels(null);
    setLocalBodyTypes(null);
    setNoResults(false); // Reset no results state

    setSelectedMakes?.(undefined);
    setHasAppliedFilters(false);

    handleResetFilter?.();
    if (isMobile) {
      setOpen(false);
    }
  }, [handleResetFilter, setSelectedMakes, isMobile]);

  // Handle Apply Filters button click
  const handleApplyFilters = useCallback(async () => {
    const newFilters: Partial<VehicleFilter> = {};

    // Add selected makes
    if (localMakes?.length) {
      newFilters.make = localMakes.map((make) => make.value);
    }

    // Add selected models
    if (localModels?.length) {
      newFilters.model = localModels.map((model) => model.value);
    }

    // Add selected body types
    if (localBodyTypes?.length) {
      newFilters.bodyType = localBodyTypes.map((bodyType) => bodyType.value);
    }

    // Add numeric filters
    if (formState.year) {
      newFilters.year = formState.year;
    }

    if (formState.price) {
      newFilters.price = formState.price;
    }

    // Set that we've applied filters
    setHasAppliedFilters(true);

    // Set loading state
    setIsFiltering(true);

    // Apply all filters and check results
    try {
      const results = await handleFilterChange(newFilters);

      // Check if we got no results
      if (!results || results.length === 0) {
        setNoResults(true);
      } else {
        setNoResults(false);
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      setNoResults(true);
    } finally {
      setIsFiltering(false);
    }
  }, [formState, localMakes, localModels, localBodyTypes, handleFilterChange]);

  // Handle local form changes without applying filters
  const updateFormState = useCallback((field: string, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Update noResults state based on resultsCount prop
  useEffect(() => {
    if (hasAppliedFilters && !isLoading && resultsCount === 0) {
      setNoResults(true);
    } else if (resultsCount && resultsCount > 0) {
      setNoResults(false);
    }
  }, [hasAppliedFilters, isLoading, resultsCount]);

  // For mobile devices, we don't render the filter component directly
  // Instead, the parent component will include our button in the labels container
  // and we'll just render the bottom sheet
  if (isMobile) {
    return (
      <>
        <Modal isOpen={open} onClose={() => setOpen(false)} size="full" className="!z-100000 rounded-none">
          <SectionTitle title="Car Filters" showButton={false} showIcon={false} className="!mb-3" />

          <div className="no-scrollbar h-full overflow-y-auto bg-white">
            <div className="py-4">
              <FilterContent
                formState={formState}
                updateFormState={updateFormState}
                localMakes={localMakes}
                setLocalMakes={setLocalMakes}
                localModels={localModels}
                setLocalModels={setLocalModels}
                localBodyTypes={localBodyTypes}
                setLocalBodyTypes={setLocalBodyTypes}
                handleApplyFilters={handleApplyFilters}
                handleReset={handleReset}
                hasAppliedFilters={hasAppliedFilters}
                isFiltering={isFiltering}
                isLoading={isLoading}
                noResults={noResults}
                resultsCount={resultsCount}
                setSelectedMakes={setSelectedMakes}
                makesOptions={makesOptions as any}
                modelsOptions={modelsOptions as any}
                bodyTypeOptions={bodyTypeOptions as unknown as BodyTypeOption[]}
                isMakesOptionsLoading={isMakesOptionsLoading}
                isModelsOptionsLoading={isModelsOptionsLoading}
                isBodyTypeOptionsLoading={isBodyTypeOptionsLoading}
                isMobile={true}
                onClose={() => setOpen(false)}
              />
            </div>
          </div>
        </Modal>
      </>
    );
  }

  // Desktop view
  return (
    <div className="relative flex h-[840px] min-w-[350px] max-w-[380px] flex-1 flex-col overflow-hidden rounded-3xl border border-2 border-dashed border-primary bg-white p-5 lg:h-[900px]">
      <FilterContent
        formState={formState}
        updateFormState={updateFormState}
        localMakes={localMakes}
        setLocalMakes={setLocalMakes}
        localModels={localModels}
        setLocalModels={setLocalModels}
        localBodyTypes={localBodyTypes}
        setLocalBodyTypes={setLocalBodyTypes}
        handleApplyFilters={handleApplyFilters}
        handleReset={handleReset}
        hasAppliedFilters={hasAppliedFilters}
        isFiltering={isFiltering}
        isLoading={isLoading}
        noResults={noResults}
        resultsCount={resultsCount}
        setSelectedMakes={setSelectedMakes}
        makesOptions={makesOptions as any}
        modelsOptions={modelsOptions as any}
        bodyTypeOptions={bodyTypeOptions as unknown as BodyTypeOption[]}
        isMakesOptionsLoading={isMakesOptionsLoading}
        isModelsOptionsLoading={isModelsOptionsLoading}
        isBodyTypeOptionsLoading={isBodyTypeOptionsLoading}
        isMobile={false}
      />
    </div>
  );
};

export default CompareCarFilters;
